/** Geometry + project-state helpers for the corner-rounding sub-tool
 *  (issue #100). A ConstructionLine that carries `cornerRounding`
 *  metadata stores its un-rounded source geometry as `sharpPathData`
 *  and a list of `{ pathIndex, segmentIndex, radius }` entries. On
 *  every commit, `deriveRoundedCorners` re-runs `applyCornerRounding`
 *  to refresh the line's visible `pathData`, so any edit that updates
 *  `sharpPathData` (e.g. a whole-line drag that translates the source)
 *  keeps the rounded view in sync without callers having to remember.
 *
 *  Corners are inscribed circular arcs: at a corner anchor with
 *  straight segments leading to its neighbours, the algorithm walks
 *  back `r / tan(α/2)` mm along each segment (where α is the corner's
 *  interior half-angle), drops two new anchors at those tangent
 *  points, and joins them with a cubic-bezier approximation of the
 *  arc. The handle length uses the standard
 *  `(4/3) * tan(arcAngle/4) * r` formula so a 90° corner produces the
 *  textbook 0.5523·r handle. */

import type paper from "paper";
import type { ConstructionLine, Project } from "./Project";
import { projectConstructionLines } from "./Project";

export interface CornerRoundingEntry {
  pathIndex: number;
  segmentIndex: number;
  radius: number;
}

/** Re-derive `pathData` from `sharpPathData` for every line that
 *  carries `cornerRounding` metadata. Returns the input project
 *  unchanged when no line needs an update so callers can cheaply
 *  skip re-snapshotting. */
export function deriveRoundedCorners(
  project: Project,
  scope: paper.PaperScope,
): Project {
  const lines = projectConstructionLines(project);
  if (lines.length === 0) return project;
  let changed = false;
  const next: ConstructionLine[] = [];
  for (const line of lines) {
    const rounding = line.cornerRounding;
    if (!rounding || rounding.corners.length === 0) {
      next.push(line);
      continue;
    }
    const rounded = applyCornerRounding(
      rounding.sharpPathData,
      rounding.corners,
      scope,
    );
    // Fall back to the sharp source if the rounding produced no
    // geometry (every entry rejected because the anchor lookups
    // failed, or the path doesn't parse). Better an un-rounded line
    // than a vanished one.
    const effective = rounded ?? rounding.sharpPathData;
    if (effective === line.pathData) {
      next.push(line);
      continue;
    }
    next.push({ ...line, pathData: effective });
    changed = true;
  }
  if (!changed) return project;
  return { ...project, constructionLines: next };
}

/** Give a construction line new geometry. `sharpPathData` is the line's
 *  un-rounded outline, already in world coordinates (any tilt baked in).
 *
 *  Use this whenever an edit writes a line's shape. A line with
 *  `cornerRounding` keeps its real geometry in `sharpPathData`, and
 *  `deriveRoundedCorners` rebuilds `pathData` from it on every commit, so
 *  writing only `pathData` + `primitive` gets the edit silently undone on
 *  commit while the primitive keeps the new size: the drawn shape and the
 *  panel's numbers then disagree for good (#1165). This writes the sharp
 *  source and the rounded `pathData` together, so the commit is already
 *  consistent and the derive pass has nothing to undo. Translation and
 *  rotation have their own helpers (`translateConstructionLine`,
 *  `rotateConstructionLine`) that already carry the sharp source. */
export function setConstructionGeometry(
  line: ConstructionLine,
  sharpPathData: string,
  primitive: ConstructionLine["primitive"],
  scope: paper.PaperScope,
): ConstructionLine {
  const rounding = line.cornerRounding;
  if (!rounding) return { ...line, pathData: sharpPathData, primitive };
  const rounded =
    rounding.corners.length > 0
      ? applyCornerRounding(sharpPathData, rounding.corners, scope)
      : null;
  return {
    ...line,
    pathData: rounded ?? sharpPathData,
    primitive,
    cornerRounding: { ...rounding, sharpPathData },
  };
}

/** Bring a rounded rectangle / ellipse's `primitive` box back in line with
 *  the shape it actually draws.
 *
 *  Before #1165, a canvas resize or a snap-chain move of a rounded line
 *  updated its `primitive` but not `cornerRounding.sharpPathData`, so the
 *  rounding pass redrew the old shape while the Properties panel showed the
 *  new size, and saved files kept the mismatch. This repairs those files on
 *  load by trusting the drawn shape: every part, stitch line and hole was
 *  built against what was on screen, so nothing else moves. Only unrotated
 *  lines are checked, because a rotated line's primitive is its un-rotated
 *  box and can't be compared with the drawn bounds directly. Returns the
 *  input project unchanged when nothing needs repair. */
export function reconcileRoundedPrimitives(
  project: Project,
  scope: paper.PaperScope,
): Project {
  const lines = projectConstructionLines(project);
  if (lines.length === 0) return project;
  const EPS = 1e-3;
  let changed = false;
  const next = lines.map((line) => {
    const prim = line.primitive;
    const sharp = line.cornerRounding?.sharpPathData;
    if (!prim || !sharp) return line;
    if (prim.kind !== "rectangle" && prim.kind !== "ellipse") return line;
    if (Math.abs(line.rotation ?? 0) > 1e-6) return line;
    let b: paper.Rectangle;
    try {
      const cp = new scope.CompoundPath({ pathData: sharp, insert: false });
      b = cp.bounds;
    } catch {
      return line;
    }
    if (!(b.width > 0) || !(b.height > 0)) return line;
    if (
      Math.abs(b.x - prim.x) < EPS &&
      Math.abs(b.y - prim.y) < EPS &&
      Math.abs(b.width - prim.width) < EPS &&
      Math.abs(b.height - prim.height) < EPS
    ) {
      return line;
    }
    changed = true;
    return {
      ...line,
      primitive: { ...prim, x: b.x, y: b.y, width: b.width, height: b.height },
    };
  });
  if (!changed) return project;
  return { ...project, constructionLines: next };
}

/** Insert inscribed circular arcs at the listed corners and return
 *  the resulting SVG pathData. Each entry's `(pathIndex, segmentIndex)`
 *  identifies a corner anchor in the *sharp* source path; the result
 *  replaces that anchor with two new anchors at the tangent points,
 *  joined by a cubic-bezier arc. Corners whose neighbours are curved
 *  (any bezier handles present), whose segments are too short to fit
 *  the requested radius (clamped to half the shorter neighbour
 *  segment), or which are collinear / reversed are passed through
 *  un-rounded. Returns `null` when the source path is empty / fails
 *  to parse. */
export function applyCornerRounding(
  sharpPathData: string,
  corners: ReadonlyArray<CornerRoundingEntry>,
  scope: paper.PaperScope,
): string | null {
  if (!sharpPathData) return null;
  scope.activate();
  let sourceCp: paper.CompoundPath;
  try {
    sourceCp = new scope.CompoundPath({
      pathData: sharpPathData,
      insert: false,
    });
  } catch {
    return null;
  }
  const subpaths: paper.Path[] =
    sourceCp.children && sourceCp.children.length > 0
      ? (sourceCp.children as paper.Path[])
      : [sourceCp as unknown as paper.Path];

  const cornersByPath = new Map<number, Map<number, number>>();
  for (const c of corners) {
    if (!isFinite(c.radius) || c.radius <= 0) continue;
    let perPath = cornersByPath.get(c.pathIndex);
    if (!perPath) {
      perPath = new Map<number, number>();
      cornersByPath.set(c.pathIndex, perPath);
    }
    perPath.set(c.segmentIndex, c.radius);
  }

  const outCp = new scope.CompoundPath({ insert: false });
  for (let pi = 0; pi < subpaths.length; pi++) {
    const sub = subpaths[pi]!;
    const cornerMap = cornersByPath.get(pi);
    const outPath = new scope.Path({ insert: false });
    outPath.closed = sub.closed;
    const n = sub.segments.length;
    for (let si = 0; si < n; si++) {
      const seg = sub.segments[si]!;
      const radius = cornerMap?.get(si);
      const hasPrev = sub.closed || si > 0;
      const hasNext = sub.closed || si < n - 1;
      if (
        radius === undefined ||
        radius <= 0 ||
        !hasPrev ||
        !hasNext
      ) {
        outPath.add(
          new scope.Segment(
            seg.point.clone(),
            seg.handleIn.clone(),
            seg.handleOut.clone(),
          ),
        );
        continue;
      }
      const prev = sub.segments[(si - 1 + n) % n]!;
      const next = sub.segments[(si + 1) % n]!;
      // The arc-fit math below assumes straight neighbour segments;
      // bezier handles in the corner's incoming / outgoing direction
      // would distort the tangent geometry. Bail out and pass the
      // anchor through un-rounded.
      const incomingCurved =
        !seg.handleIn.isZero() || !prev.handleOut.isZero();
      const outgoingCurved =
        !seg.handleOut.isZero() || !next.handleIn.isZero();
      if (incomingCurved || outgoingCurved) {
        outPath.add(
          new scope.Segment(
            seg.point.clone(),
            seg.handleIn.clone(),
            seg.handleOut.clone(),
          ),
        );
        continue;
      }
      const toPrev = prev.point.subtract(seg.point);
      const toNext = next.point.subtract(seg.point);
      const prevLen = toPrev.length;
      const nextLen = toNext.length;
      if (prevLen < 1e-6 || nextLen < 1e-6) {
        outPath.add(new scope.Segment(seg.point.clone()));
        continue;
      }
      const prevDir = toPrev.divide(prevLen);
      const nextDir = toNext.divide(nextLen);
      const bis = prevDir.add(nextDir);
      const bisLen = bis.length;
      if (bisLen < 1e-6) {
        // Reversed (180°) corner — no inscribed arc is well-defined.
        outPath.add(new scope.Segment(seg.point.clone()));
        continue;
      }
      const cosHalf = Math.max(-1, Math.min(1, bis.dot(prevDir) / bisLen));
      const halfAngle = Math.acos(cosHalf);
      if (halfAngle < 1e-3 || halfAngle > Math.PI - 1e-3) {
        // Collinear or reversed — degenerate, leave the corner sharp.
        outPath.add(new scope.Segment(seg.point.clone()));
        continue;
      }
      const tanHalf = Math.tan(halfAngle);
      const desiredTan = radius / tanHalf;
      // Cap so neither tangent point passes the midpoint of its
      // neighbour segment. With both ends of a segment clamped to
      // 0.5·len each, two adjacent rounded corners can still abut
      // (their tangent points meet) without overlapping.
      const maxTan = Math.min(prevLen, nextLen) * 0.5;
      const tanDist = Math.min(desiredTan, maxTan);
      const actualRadius = tanDist * tanHalf;
      const tanPrevPt = seg.point.add(prevDir.multiply(tanDist));
      const tanNextPt = seg.point.add(nextDir.multiply(tanDist));
      // Cubic-bezier handle length for a circular arc subtending
      // `arcAngle` = π − 2·halfAngle. Magic factor 4/3·tan(θ/4)
      // gives the textbook 0.5523·r at θ = π/2.
      const arcAngle = Math.PI - 2 * halfAngle;
      const handleLen =
        (4 / 3) * Math.tan(arcAngle / 4) * actualRadius;
      // Handles point along the path-travel direction at each tangent
      // point. Just past tanPrev (heading toward tanNext) the tangent
      // continues away from `prev` — i.e. opposite to prevDir. At
      // tanNext, arriving from the arc, the tangent is along nextDir,
      // so handleIn (which addresses the control point behind the
      // anchor) goes the opposite way.
      const handleOut = prevDir.multiply(-handleLen);
      const handleIn = nextDir.multiply(-handleLen);
      outPath.add(
        new scope.Segment(
          tanPrevPt,
          new scope.Point(0, 0),
          handleOut,
        ),
      );
      outPath.add(
        new scope.Segment(
          tanNextPt,
          handleIn,
          new scope.Point(0, 0),
        ),
      );
    }
    outCp.addChild(outPath);
  }

  const out = outCp.pathData;
  outCp.remove();
  sourceCp.remove();
  return out && out.length > 0 ? out : null;
}

/** Geometric description of a round-eligible corner: the corner
 *  point, the unit vector along the interior-angle bisector
 *  (pointing INTO the corner — i.e. toward the arc centre), the
 *  interior half-angle in radians, and the maximum inscribed radius
 *  the corner can fit before the tangent points would exceed half a
 *  neighbour segment.
 *
 *  The arc centre for a corner with radius r sits at
 *  `cornerPoint + bisectorDir * (r / sin(halfAngle))`. The Round
 *  sub-tool renders the drag handle at that position and recovers
 *  the radius from the cursor's projection back onto the bisector
 *  during a drag (`handleDist * sin(halfAngle)`). */
export interface CornerGeometry {
  cornerPoint: { x: number; y: number };
  bisectorDir: { x: number; y: number };
  halfAngle: number;
  maxRadius: number;
}

/** Read the full geometry of the corner at
 *  `(pathIndex, segmentIndex)` of `sharpPathData`. Returns null
 *  when the anchor doesn't exist, doesn't qualify as a corner
 *  (open-path endpoint, collinear segments, neighbours with bezier
 *  handles), or the path fails to parse. Used by both the Round
 *  sub-tool's hit-test and the Scene's handle renderer so the two
 *  agree on where the handle sits. */
export function getCornerGeometry(
  sharpPathData: string,
  pathIndex: number,
  segmentIndex: number,
  scope: paper.PaperScope,
): CornerGeometry | null {
  if (!sharpPathData) return null;
  let cp: paper.CompoundPath;
  try {
    cp = new scope.CompoundPath({ pathData: sharpPathData, insert: false });
  } catch {
    return null;
  }
  const subpaths: paper.Path[] =
    cp.children && cp.children.length > 0
      ? (cp.children as paper.Path[])
      : [cp as unknown as paper.Path];
  const sub = subpaths[pathIndex];
  if (!sub) {
    cp.remove();
    return null;
  }
  const n = sub.segments.length;
  const seg = sub.segments[segmentIndex];
  if (!seg) {
    cp.remove();
    return null;
  }
  const hasPrev = sub.closed || segmentIndex > 0;
  const hasNext = sub.closed || segmentIndex < n - 1;
  if (!hasPrev || !hasNext) {
    cp.remove();
    return null;
  }
  const prev = sub.segments[(segmentIndex - 1 + n) % n]!;
  const next = sub.segments[(segmentIndex + 1) % n]!;
  const incomingCurved =
    !seg.handleIn.isZero() || !prev.handleOut.isZero();
  const outgoingCurved =
    !seg.handleOut.isZero() || !next.handleIn.isZero();
  if (incomingCurved || outgoingCurved) {
    cp.remove();
    return null;
  }
  const toPrev = prev.point.subtract(seg.point);
  const toNext = next.point.subtract(seg.point);
  const prevLen = toPrev.length;
  const nextLen = toNext.length;
  if (prevLen < 1e-6 || nextLen < 1e-6) {
    cp.remove();
    return null;
  }
  const prevDir = toPrev.divide(prevLen);
  const nextDir = toNext.divide(nextLen);
  const bis = prevDir.add(nextDir);
  const bisLen = bis.length;
  if (bisLen < 1e-6) {
    cp.remove();
    return null;
  }
  const bisDir = bis.divide(bisLen);
  const cosHalf = Math.max(-1, Math.min(1, bisDir.dot(prevDir)));
  const halfAngle = Math.acos(cosHalf);
  const cornerPoint = { x: seg.point.x, y: seg.point.y };
  const bisectorDir = { x: bisDir.x, y: bisDir.y };
  cp.remove();
  if (halfAngle < 1e-3 || halfAngle > Math.PI - 1e-3) return null;
  const maxTan = Math.min(prevLen, nextLen) * 0.5;
  const maxRadius = maxTan * Math.tan(halfAngle);
  return { cornerPoint, bisectorDir, halfAngle, maxRadius };
}

/** Distance from the corner anchor to the arc centre for an
 *  inscribed circle of `radius`. Formula: `r / sin(halfAngle)` —
 *  e.g. a 90° corner ⇒ `r·√2`. Used internally; UI callers should
 *  reach for `handleDistanceFromCorner` instead so the rest-offset
 *  for the draggable knob (#100) is baked in. */
export function arcCenterDistance(
  geom: Pick<CornerGeometry, "halfAngle">,
  radius: number,
): number {
  return radius / Math.sin(geom.halfAngle);
}

/** "Phantom" minimum-radius offset for the round-corner handle
 *  knob (issue #100). The knob's rest position — what the user
 *  sees on an un-rounded corner — is the arc centre for this
 *  pretend radius, so the dot is always visibly clear of the
 *  corner stroke (a knob sitting ON the corner would disappear
 *  behind the line). At rest the actual rounding radius is 0; as
 *  the user drags the knob further from the corner the radius
 *  grows. The knob never returns past this rest position, so the
 *  "drag past the corner to un-round" gesture is implemented by
 *  dragging the knob back to its rest spot. */
export const HANDLE_REST_OFFSET_RADIUS_MM = 3;

/** Visual distance from corner to handle dot for the given
 *  rounding `radius` (issue #100). Baked-in rest offset means a
 *  radius of 0 still produces a non-zero distance — the knob
 *  parks at the arc centre for `HANDLE_REST_OFFSET_RADIUS_MM`
 *  rather than collapsing onto the corner. */
export function handleDistanceFromCorner(
  geom: CornerGeometry,
  radius: number,
): number {
  return arcCenterDistance(
    geom,
    HANDLE_REST_OFFSET_RADIUS_MM + Math.max(0, radius),
  );
}

/** Inverse of `handleDistanceFromCorner`: given a handle position
 *  somewhere inside the corner, project it onto the bisector and
 *  recover the rounding radius implied. The rest offset is
 *  subtracted before converting to radius, so the knob's rest
 *  position is treated as radius=0 and any drag inward of the
 *  rest position keeps clamping to 0 (the un-rounded gesture).
 *  Result is never negative. */
export function radiusFromHandlePosition(
  geom: CornerGeometry,
  handlePos: { x: number; y: number },
): number {
  const dx = handlePos.x - geom.cornerPoint.x;
  const dy = handlePos.y - geom.cornerPoint.y;
  const handleDist = Math.max(
    0,
    dx * geom.bisectorDir.x + dy * geom.bisectorDir.y,
  );
  const restOffset = arcCenterDistance(geom, HANDLE_REST_OFFSET_RADIUS_MM);
  const arcCenter = Math.max(0, handleDist - restOffset);
  return arcCenter * Math.sin(geom.halfAngle);
}

/** Read a corner anchor's interior half-angle (radians) from the
 *  sharp source path. Returns null when the anchor doesn't exist,
 *  doesn't qualify as a corner (open-path endpoint, collinear
 *  segments, neighbours with bezier handles), or the path fails to
 *  parse. Thin wrapper around `getCornerGeometry` kept around for
 *  callers that only need the angle. */
/** Enumerate every round-eligible corner anchor in
 *  `sharpPathData`. A corner is round-eligible when
 *  `getCornerGeometry` returns non-null for it and its
 *  `maxRadius` is at least `minRadius` (callers pass the same
 *  threshold the Round sub-tool uses so we skip corners no drag
 *  could ever commit on). Used by the "round all corners" branch
 *  of the Construction tool's Round sub-mode (#100 follow-up). */
export function enumerateRoundableCorners(
  sharpPathData: string,
  scope: paper.PaperScope,
  minRadius = 0.5,
): Array<{ pathIndex: number; segmentIndex: number }> {
  if (!sharpPathData) return [];
  let cp: paper.CompoundPath;
  try {
    cp = new scope.CompoundPath({ pathData: sharpPathData, insert: false });
  } catch {
    return [];
  }
  const subpaths: paper.Path[] =
    cp.children && cp.children.length > 0
      ? (cp.children as paper.Path[])
      : [cp as unknown as paper.Path];
  const out: Array<{ pathIndex: number; segmentIndex: number }> = [];
  for (let pi = 0; pi < subpaths.length; pi++) {
    const sub = subpaths[pi]!;
    for (let si = 0; si < sub.segments.length; si++) {
      const geom = getCornerGeometry(sharpPathData, pi, si, scope);
      if (!geom) continue;
      if (geom.maxRadius < minRadius) continue;
      out.push({ pathIndex: pi, segmentIndex: si });
    }
  }
  cp.remove();
  return out;
}

/** A `cornerRounding` block that rounds EVERY roundable corner of
 *  `sharpPathData` at `radiusMm`, each clamped to what its corner can take —
 *  the same entries the Round tools' "all corners" writes, so the result stays
 *  adjustable (and can be taken back to sharp) exactly like a hand-rounded
 *  shape. Undefined when the radius is below `minRadius` or nothing can round.
 *  Draws a standard-size shape with its corners already rounded (#1069); fits
 *  `ConstructionLine`, `Piece` and `Cutout` alike. */
export function roundAllCorners(
  sharpPathData: string,
  radiusMm: number,
  scope: paper.PaperScope,
  minRadius = 0.5,
): NonNullable<ConstructionLine["cornerRounding"]> | undefined {
  if (!(radiusMm >= minRadius)) return undefined;
  const corners: NonNullable<ConstructionLine["cornerRounding"]>["corners"] =
    [];
  for (const c of enumerateRoundableCorners(sharpPathData, scope, minRadius)) {
    const geom = getCornerGeometry(
      sharpPathData,
      c.pathIndex,
      c.segmentIndex,
      scope,
    );
    if (!geom) continue;
    corners.push({ ...c, radius: Math.min(radiusMm, geom.maxRadius) });
  }
  return corners.length > 0 ? { sharpPathData, corners } : undefined;
}


export function getCornerHalfAngle(
  sharpPathData: string,
  pathIndex: number,
  segmentIndex: number,
  scope: paper.PaperScope,
): number | null {
  return (
    getCornerGeometry(sharpPathData, pathIndex, segmentIndex, scope)
      ?.halfAngle ?? null
  );
}

/** Maximum radius that fits inside the corner at
 *  `(pathIndex, segmentIndex)` of `sharpPathData`. Hits the smaller
 *  of (a) the requested geometric limit or (b) half the shorter
 *  neighbour segment length × tan(half-angle), which is the same
 *  cap `applyCornerRounding` enforces. Returns null for non-corner
 *  anchors. */
export function getCornerMaxRadius(
  sharpPathData: string,
  pathIndex: number,
  segmentIndex: number,
  scope: paper.PaperScope,
): number | null {
  return (
    getCornerGeometry(sharpPathData, pathIndex, segmentIndex, scope)
      ?.maxRadius ?? null
  );
}
